// Signup: email code + password. Login: email + password only.
"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { FormEvent, useEffect, useState } from "react";
import { Password_Field } from "@/components/auth/password-field";
import {
  Auth_Alert_Error,
  Auth_Alert_Info,
  Auth_Btn_Ghost,
  Auth_Btn_Primary,
  Auth_Input_Class,
  Auth_Label_Class,
} from "@/components/auth/auth-styles";
import { Api_Paths } from "@/lib/api/paths";
import { Auth_Access_Set } from "@/lib/auth/client/session";
import { Codes_Ttl_Seconds } from "@/lib/auth/constants";

type Auth_Form_Mode = "login" | "signup";
type Auth_Form_Step = "details" | "code";
type Auth_Form_Variant = "page" | "modal";

type Auth_Form_Props = {
  Mode: Auth_Form_Mode;
  Variant?: Auth_Form_Variant;
  Next_Path?: string;
  On_Success?: () => void;
  On_Mode_Change?: (Mode: Auth_Form_Mode) => void;
};

const Password_Min_Length = 8;

export function Auth_Form({
  Mode,
  Variant = "page",
  Next_Path: Next_Path_Prop,
  On_Success,
  On_Mode_Change,
}: Auth_Form_Props) {
  const Router = useRouter();
  const Search_Params = useSearchParams();
  const Next_Path = Next_Path_Prop ?? Search_Params.get("next") ?? "/dashboard";

  const [Step, Step_Set] = useState<Auth_Form_Step>("details");
  const [Email, Email_Set] = useState("");
  const [First_Name, First_Name_Set] = useState("");
  const [Last_Name, Last_Name_Set] = useState("");
  const [Password, Password_Set] = useState("");
  const [Confirm_Password, Confirm_Password_Set] = useState("");
  const [Code, Code_Set] = useState("");
  const [Remember_Me, Remember_Me_Set] = useState(true);
  const [Error_Message, Error_Set] = useState<string | null>(null);
  const [Info_Message, Info_Set] = useState<string | null>(null);
  const [Is_Loading, Loading_Set] = useState(false);
  const [Cooldown_Seconds, Cooldown_Set] = useState(0);

  const Is_Signup = Mode === "signup";
  const Is_Modal = Variant === "modal";
  const Field_Prefix = Is_Signup ? "signup" : "login";
  const Form_Space = Is_Modal ? "space-y-3.5" : "space-y-4";

  useEffect(() => {
    if (Cooldown_Seconds <= 0) {
      return;
    }

    const Timer = window.setInterval(() => {
      Cooldown_Set((Seconds) => (Seconds <= 1 ? 0 : Seconds - 1));
    }, 1000);

    return () => window.clearInterval(Timer);
  }, [Cooldown_Seconds]);

  const Passwords_Match = Password === Confirm_Password;
  const Signup_Details_Ready =
    First_Name.trim().length > 0 &&
    Last_Name.trim().length > 0 &&
    Email.trim().length > 0 &&
    Password.length >= Password_Min_Length &&
    Confirm_Password.length >= Password_Min_Length &&
    Passwords_Match;

  const Login_Ready = Email.trim().length > 0 && Password.length > 0;
  const Code_Ready = /^\d{6}$/.test(Code.trim());

  async function Auth_Complete_Handle(Access_Token: string) {
    Auth_Access_Set(Access_Token);

    if (On_Success) {
      On_Success();
    }

    const Path_Next = Next_Path.trim() || "/dashboard";
    Router.push(Path_Next);
    Router.refresh();
  }

  async function Signup_Send_Code_Handle() {
    Error_Set(null);
    Info_Set(null);

    if (!Passwords_Match) {
      Error_Set("Passwords do not match");
      return;
    }

    Loading_Set(true);

    try {
      const Response = await fetch(Api_Paths.V1.Auth.Signup_Send_Code, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: Email.trim(),
          first_name: First_Name.trim(),
          last_name: Last_Name.trim(),
          password: Password,
        }),
      });

      const Body = (await Response.json()) as {
        error?: string;
        retry_after_seconds?: number;
        expires_in_seconds?: number;
        message?: string;
      };

      if (!Response.ok) {
        if (Response.status === 429 && Body.retry_after_seconds) {
          Cooldown_Set(Body.retry_after_seconds);
        }
        Error_Set(Body.error ?? "Failed to send code");
        return;
      }

      Cooldown_Set(Body.expires_in_seconds ?? Codes_Ttl_Seconds);
      Info_Set(Body.message ?? "Verification code sent. Check your email.");
      Step_Set("code");
    } catch {
      Error_Set("Network error. Try again.");
    } finally {
      Loading_Set(false);
    }
  }

  async function Signup_Verify_Code_Handle(Event: FormEvent<HTMLFormElement>) {
    Event.preventDefault();
    Error_Set(null);
    Loading_Set(true);

    try {
      const Response = await fetch(Api_Paths.V1.Auth.Signup, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: Email.trim(),
          code: Code.trim(),
        }),
      });

      const Body = (await Response.json()) as { error?: string; accessToken?: string };

      if (!Response.ok || !Body.accessToken) {
        Error_Set(Body.error ?? "Invalid or expired code");
        return;
      }

      await Auth_Complete_Handle(Body.accessToken);
    } catch {
      Error_Set("Network error. Try again.");
    } finally {
      Loading_Set(false);
    }
  }

  async function Login_Handle(Event: FormEvent<HTMLFormElement>) {
    Event.preventDefault();
    Error_Set(null);
    Loading_Set(true);

    try {
      const Response = await fetch(Api_Paths.V1.Auth.Login, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: Email.trim(),
          password: Password,
          rememberMe: Remember_Me,
        }),
      });

      const Body = (await Response.json()) as { error?: string; accessToken?: string };

      if (!Response.ok || !Body.accessToken) {
        Error_Set(Body.error ?? "Invalid email or password");
        return;
      }

      await Auth_Complete_Handle(Body.accessToken);
    } catch {
      Error_Set("Network error. Try again.");
    } finally {
      Loading_Set(false);
    }
  }

  const Mode_Switch =
    On_Mode_Change && !Is_Modal ? (
      <p className="text-center text-xs text-pq-fg-subtle">
        {Is_Signup ? (
          <>
            Already have an account?{" "}
            <button type="button" onClick={() => On_Mode_Change("login")} className={Auth_Btn_Ghost}>
              Sign in
            </button>
          </>
        ) : (
          <>
            No account yet?{" "}
            <button type="button" onClick={() => On_Mode_Change("signup")} className={Auth_Btn_Ghost}>
              Create one
            </button>
          </>
        )}
      </p>
    ) : null;

  if (!Is_Signup) {
    return (
      <form onSubmit={Login_Handle} className={Form_Space} data-auth-flow="password-login-v1">
        <div>
          <label htmlFor={`${Field_Prefix}-email`} className={Auth_Label_Class}>
            Email
          </label>
          <input
            id={`${Field_Prefix}-email`}
            type="email"
            autoComplete="email"
            required
            value={Email}
            onChange={(Event) => Email_Set(Event.target.value)}
            className={Auth_Input_Class}
            placeholder="you@example.com"
          />
        </div>

        <Password_Field
          Id={`${Field_Prefix}-password`}
          Label="Password"
          Value={Password}
          On_Change={Password_Set}
          AutoComplete="current-password"
          Placeholder="Your password"
          Show_Strength
        />

        <label
          htmlFor={`${Field_Prefix}-remember`}
          className="flex cursor-pointer items-center gap-2.5 text-sm text-slate-300"
        >
          <input
            id={`${Field_Prefix}-remember`}
            type="checkbox"
            checked={Remember_Me}
            onChange={(Event) => Remember_Me_Set(Event.target.checked)}
            className="h-4 w-4 rounded border-pq-border-strong bg-pq-bg-elevated text-amber-600 focus:ring-amber-400/30"
          />
          Remember me
        </label>

        {Error_Message ? (
          <p className={Auth_Alert_Error} role="alert">
            {Error_Message}
          </p>
        ) : null}

        <button type="submit" disabled={Is_Loading || !Login_Ready} className={Auth_Btn_Primary}>
          {Is_Loading ? "Signing in…" : "Sign in"}
        </button>

        {Mode_Switch}
      </form>
    );
  }

  if (Step === "code") {
    return (
      <form onSubmit={Signup_Verify_Code_Handle} className={Form_Space} data-auth-flow="signup-code-v1">
        <p className="text-xs leading-relaxed text-pq-fg-subtle">
          Code sent to <span className="font-medium text-slate-200">{Email}</span>
        </p>

        <div>
          <label htmlFor={`${Field_Prefix}-code`} className={Auth_Label_Class}>
            6-digit code
          </label>
          <input
            id={`${Field_Prefix}-code`}
            name="auth-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{6}"
            maxLength={6}
            required
            value={Code}
            onChange={(Event) => Code_Set(Event.target.value.replace(/\D/g, "").slice(0, 6))}
            className={Auth_Input_Class}
            placeholder="123456"
          />
        </div>

        {Error_Message ? (
          <p className={Auth_Alert_Error} role="alert">
            {Error_Message}
          </p>
        ) : null}

        {Info_Message ? (
          <p className={Auth_Alert_Info} role="status">
            {Info_Message}
          </p>
        ) : null}

        <button type="submit" disabled={Is_Loading || !Code_Ready} className={Auth_Btn_Primary}>
          {Is_Loading ? "Verifying…" : "Create account"}
        </button>

        <div className="flex flex-col gap-1.5 text-center text-xs text-pq-fg-subtle">
          <button
            type="button"
            disabled={Is_Loading || Cooldown_Seconds > 0}
            onClick={Signup_Send_Code_Handle}
            className={Auth_Btn_Ghost}
          >
            {Cooldown_Seconds > 0 ? `Resend code in ${Cooldown_Seconds}s` : "Resend code"}
          </button>
          <button
            type="button"
            onClick={() => {
              Step_Set("details");
              Code_Set("");
              Error_Set(null);
            }}
            className="text-pq-fg-subtle transition hover:text-slate-300"
          >
            Change email
          </button>
        </div>
      </form>
    );
  }

  return (
    <div className={Form_Space} data-auth-flow="signup-details-v1">
      <div className="grid gap-3.5 sm:grid-cols-2">
        <div>
          <label htmlFor={`${Field_Prefix}-first-name`} className={Auth_Label_Class}>
            First name
          </label>
          <input
            id={`${Field_Prefix}-first-name`}
            type="text"
            autoComplete="given-name"
            required
            value={First_Name}
            onChange={(Event) => First_Name_Set(Event.target.value)}
            className={Auth_Input_Class}
            placeholder="Jane"
          />
        </div>
        <div>
          <label htmlFor={`${Field_Prefix}-last-name`} className={Auth_Label_Class}>
            Last name
          </label>
          <input
            id={`${Field_Prefix}-last-name`}
            type="text"
            autoComplete="family-name"
            required
            value={Last_Name}
            onChange={(Event) => Last_Name_Set(Event.target.value)}
            className={Auth_Input_Class}
            placeholder="Doe"
          />
        </div>
      </div>

      <div>
        <label htmlFor={`${Field_Prefix}-email`} className={Auth_Label_Class}>
          Email
        </label>
        <input
          id={`${Field_Prefix}-email`}
          type="email"
          autoComplete="email"
          required
          value={Email}
          onChange={(Event) => Email_Set(Event.target.value)}
          className={Auth_Input_Class}
          placeholder="you@example.com"
        />
      </div>

      <Password_Field
        Id={`${Field_Prefix}-password`}
        Label="Password"
        Value={Password}
        On_Change={Password_Set}
        AutoComplete="new-password"
        Placeholder="At least 8 characters"
        Min_Length={Password_Min_Length}
        Show_Strength
      />

      <Password_Field
        Id={`${Field_Prefix}-confirm-password`}
        Label="Confirm password"
        Value={Confirm_Password}
        On_Change={Confirm_Password_Set}
        AutoComplete="new-password"
        Placeholder="Re-enter password"
        Min_Length={Password_Min_Length}
        Mismatch_Message={
          Confirm_Password.length > 0 && !Passwords_Match ? "Passwords do not match" : null
        }
      />

      {Error_Message ? (
        <p className={Auth_Alert_Error} role="alert">
          {Error_Message}
        </p>
      ) : null}

      {Info_Message ? (
        <p className={Auth_Alert_Info} role="status">
          {Info_Message}
        </p>
      ) : null}

      <button
        type="button"
        disabled={Is_Loading || !Signup_Details_Ready}
        onClick={Signup_Send_Code_Handle}
        className={
          Signup_Details_Ready
            ? Auth_Btn_Primary
            : "w-full rounded-lg border border-pq-border-strong bg-slate-100 px-4 py-2.5 text-sm font-semibold text-pq-fg-subtle transition disabled:cursor-not-allowed disabled:opacity-60"
        }
      >
        {Is_Loading ? "Sending…" : "Send verification code"}
      </button>

      {Mode_Switch}
    </div>
  );
}
